/* eslint-disable react-hooks/refs -- diagnostic-only ref read + write
 * inside this module-level helper. Gated behind RENDER_DEBUG; never
 * participates in rendering output. */
import { useRef } from "react";
import { RENDER_DEBUG } from "./debug-flags";

/**
 * Render-time prop-diff diagnostic. Logs a render counter plus the list
 * of prop keys whose identity changed since the last render — which is
 * what `React.memo` compares against. Lets you attribute a memo-bust to
 * the specific prop reference that flipped.
 *
 * Usage at the top of a memoized component body:
 *
 *   usePropDiffLog("MessageInput", {
 *     channelId,
 *     workspaceMembers,
 *     onSend,
 *     ...
 *   }, `ch=${channelId?.slice(-6)} dm=${isDM}`);
 *
 * Output format:
 *
 *   [MessageInput] render #42 ch=abc123 dm=false CHANGED: workspaceMembers, onSend
 *   [MessageInput] render #43 ch=abc123 dm=false INTERNAL (no prop change)
 *
 * `INTERNAL (no prop change)` means the component re-rendered without any
 * prop reference changing — usually a state setter inside the component
 * or a context update.
 *
 * Inert when `RENDER_DEBUG` is off (one ref read/write per render).
 */
export function usePropDiffLog(
  componentName: string,
  props: Record<string, unknown>,
  contextSummary = "",
): void {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  const prevPropsRef = useRef<Record<string, unknown>>({});
  if (!RENDER_DEBUG) {
    prevPropsRef.current = props;
    return;
  }
  const changed: string[] = [];
  for (const key of Object.keys(props)) {
    if (prevPropsRef.current[key] !== props[key]) changed.push(key);
  }
  prevPropsRef.current = props;
  console.debug(
    `[${componentName}] render #${renderCountRef.current}`,
    contextSummary,
    changed.length > 0
      ? `CHANGED: ${changed.join(", ")}`
      : "INTERNAL (no prop change)",
  );
}
